import { useMemo, useState } from "react";
import { Lock, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const blank = (v) => v === null || v === undefined || v === "";

export const useLocalSort = (rows, defKey, defOrder = 1) => {
  const [s, setS] = useState({ key: defKey, order: defOrder });
  const sorted = useMemo(() => [...(rows || [])].sort((a, b) => {
    const x = a[s.key];
    const y = b[s.key];
    if (blank(x) !== blank(y)) return blank(x) ? 1 : -1;
    const c = typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? ""), "en", { numeric: true, sensitivity: "base" });
    return c * s.order;
  }), [rows, s]);
  return [sorted, { ...s, onSort: (k) => setS((c) => ({ key: k, order: c.key === k ? -c.order : 1 })) }];
};

export const SortTh = ({ k, label, sort, className = "" }) => {
  if (!sort || !k) return <th className={className}>{label}</th>;
  const on = sort.key === k;
  const Icon = on ? (sort.order > 0 ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={className} aria-sort={on ? (sort.order > 0 ? "ascending" : "descending") : "none"}>
      <button type="button" data-testid={`sort-${k}`} title="Sort" onClick={(e) => { e.stopPropagation(); sort.onSort(k); }}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.12em] transition-colors hover:text-foreground ${on ? "text-primary" : ""}`}>
        {label}<Icon className={`h-3 w-3 ${on ? "" : "opacity-40"}`} />
      </button>
    </th>
  );
};

export const urlSort = (sp, setSp, defKey, textKeys = []) => {
  const key = sp.get("sort") || defKey;
  const order = Number(sp.get("order") || -1);
  return {
    key, order,
    onSort: (k) => {
      const n = new URLSearchParams(sp);
      n.set("sort", k);
      n.set("order", String(key === k ? -order : textKeys.includes(k) ? 1 : -1));
      n.delete("page");
      setSp(n);
    },
  };
};

export const STATUS_COLORS = {
  A: "#64748b", B: "#0ea5e9", C: "#f59e0b", D: "#a78bfa", E: "#ec4899", F: "#10b981", G: "#f43f5e",
};

export const StatusPill = ({ code, label, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1.5 border px-1.5 py-0.5 text-[11px] font-mono whitespace-nowrap"
    style={{ borderColor: `${STATUS_COLORS[code] || "#64748b"}66`, color: STATUS_COLORS[code] || "#9aa5b8" }}>
    <span className="h-1.5 w-1.5" style={{ background: STATUS_COLORS[code] || "#64748b" }} />
    {label || code}
  </span>
);

export const PageHeader = ({ eyebrow, title, desc, children }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6 ax-fade">
    <div>
      {eyebrow && <div className="ax-label mb-2 text-primary">{eyebrow}</div>}
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
      {desc && <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{desc}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Kpi = ({ label, value, hint, tone, testid, onClick }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="ax-panel text-left p-4 transition-colors hover:border-primary/60 disabled:cursor-default" disabled={!onClick}>
    <div className="ax-label">{label}</div>
    <div className="num text-3xl font-bold mt-2" style={{ color: tone }}>{value ?? "—"}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </button>
);

export const Restricted = ({ testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground border border-dashed px-1.5">
    <Lock className="h-3 w-3" /> Restricted
  </span>
);

export const FieldValue = ({ row, f, testid }) => {
  if (row?._restricted?.includes(f)) return <Restricted testid={testid} />;
  const v = row?.[f];
  return <span data-testid={testid} className={row?._masked?.includes(f) ? "font-mono text-muted-foreground" : ""}>{v ?? "—"}</span>;
};

export const Empty = ({ text = "Nothing here yet." }) => (
  <div className="py-14 text-center text-sm text-muted-foreground border border-dashed">{text}</div>
);

export const Pager = ({ page, total, size, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <div className="flex items-center justify-between py-3 text-xs text-muted-foreground" data-testid="pager">
      <span className="num">{total.toLocaleString("en-IN")} records · page {page}/{pages}</span>
      <div className="flex gap-2">
        <button data-testid="pager-prev" disabled={page <= 1} onClick={() => onPage(page - 1)} className="border px-3 py-1 disabled:opacity-40 hover:border-primary">Prev</button>
        <button data-testid="pager-next" disabled={page >= pages} onClick={() => onPage(page + 1)} className="border px-3 py-1 disabled:opacity-40 hover:border-primary">Next</button>
      </div>
    </div>
  );
};

export const BRANCH_LABEL = { DEL: "Delhi (FBD)", MUM: "Mumbai", UNASSIGNED: "Unassigned", "*": "All branches" };
export const BIN_ROLES = ["founder", "branch_manager", "_legacy_branch_manager", "sales_manager", "managing_partner"];
export const istDT = (s, sec = false) => (s ? new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: sec ? "medium" : "short" }) : "—");
export const BOARD_ROLES = ["founder", "managing_partner", "branch_manager", "_legacy_branch_manager", "sales_manager", "business_development", "inside_sales", "repeat_kam", "account_manager"];
